import React, { useEffect, useRef, useState } from 'react'
import DialogContent from '@mui/material/DialogContent'
import DialogActions from '@mui/material/DialogActions'
import Typography from '@mui/material/Typography'
import TextField from '@mui/material/TextField'
import { useTranslation } from 'react-i18next'
import Table from '@mui/material/Table'
import {
   Button,
   FormControl,
   FormControlLabel,
   InputLabel,
   MenuItem,
   Radio,
   RadioGroup,
   Select,
   Switch,
   TableBody,
   TableCell,
   TableHead,
   TableRow,
   Tooltip,
} from '@mui/material'
import Box from '@mui/material/Box'

import { InfinityThresholdType } from 'views/Authorized/views/Locations/types'
import { EditReflinkPayload, ReflinkType } from 'shared/store/useClientReflinks/types'
import ContainedButton from 'shared/components/Buttons/ContainedButton'
import { useEditReflinkMutation } from 'shared/store/useClientReflinks'
import OutlinedButton from 'shared/components/Buttons/OutlinedButton'
import { MarkupThresholdType } from 'shared/store/useLocations/types'
import { useProfileQuery } from 'shared/store/useProfile'
import { useLocationsQuery } from 'shared/store/useLocations'
import useCustomersQuery from 'shared/store/useCustomers'
import { CustomerContactType } from 'shared/store/useCustomers/types'

interface Props {
   handleClose: () => void
   reflinkUuid: string
   reflinkSettings?: ReflinkType
}

type EditReflinkType = {
   priceType: PriceType
   hideGrossPrices: boolean
   emailNotification: boolean
   instantOrder: boolean
   verificationMethod: 'SMS' | 'EMAIL'
   clientUuid: string
   clientContactUuid: string
}

export default function EditReflinkModalContent({ handleClose, reflinkUuid, reflinkSettings }: Props) {
   const [addReflinkData, setAddReflinkData] = useState<EditReflinkType>({
      instantOrder: false,
      clientUuid: '',
      verificationMethod: 'EMAIL',
      clientContactUuid: '',
      hideGrossPrices: false,
      emailNotification: false,
      priceType: 'NET',
   })
   const { data: customers } = useCustomersQuery()
   const [contactPersons, setContactPersons] = useState<CustomerContactType[]>([])
   const [infinityThresholdValues, setInfinityThresholdValues] = useState<InfinityThresholdType>({
      multiplier: 1,
      addend: 0,
   })
   const [thresholds, setThresholds] = useState<MarkupThresholdType[]>([])
   const { data: profileData } = useProfileQuery()
   const { data: locations } = useLocationsQuery()

   const { mutate, isLoading } = useEditReflinkMutation()
   const { t } = useTranslation(['TRANSLATION', 'CLIENTS_CARTS'])

   const priceTypeOptions = [
      { name: t('CLIENTS_CARTS:markupSettings.grossLabel'), value: 'GROSS' },
      { name: t('CLIENTS_CARTS:markupSettings.netLabel'), value: 'NET' },
   ]
   function handleChange(index: number, field: string, value: number) {
      const newThresholds = thresholds.map((item, i) => {
         if (i === index) {
            return { ...item, [field]: value }
         }
         return item
      })

      setThresholds(newThresholds)
   }

   function addThreshold() {
      const lastElement = thresholds[thresholds.length - 1]
      if (lastElement) {
         const newThresholds = [...thresholds]

         if (lastElement.end) {
            newThresholds.push({
               start: Number((lastElement.end + 0.01).toFixed(2)),
               end: Number((lastElement.end + 0.02).toFixed(2)),
               multiplier: 1,
               addend: 1,
            })
         }
         setThresholds(newThresholds)
      } else {
         setThresholds([{ start: 0, end: null, multiplier: 1, addend: 1 }])
      }
   }

   function removeLastItem() {
      const newThresholds = thresholds.slice(0, -1)
      setThresholds(newThresholds)
   }

   function getLastThresholdStartValue() {
      if (thresholds.length > 0) {
         if (thresholds[thresholds.length - 1]) {
            const endValue = thresholds[thresholds.length - 1].end
            if (endValue !== null) {
               return (endValue + 0.01).toFixed(2)
            }
            return ''
         }
      }
      return 0
   }

   function getLastThresholdEndValue() {
      const lastElement = thresholds[thresholds.length - 1]
      if (lastElement) {
         const { end } = lastElement
         if (end) {
            return (end + 0.01).toFixed(2)
         }
         return 0
      }
      return 0
   }

   function editReflink() {
      const payload: EditReflinkPayload = {
         ...addReflinkData,
         markupSettings: {
            thresholds: [
               ...thresholds,
               {
                  ...infinityThresholdValues,
                  start: Number(getLastThresholdEndValue()),
                  end: null,
               },
            ],
            priceType: addReflinkData.priceType,
         },
      }
      mutate(
         { payload, reflinkUuid },
         {
            onSuccess: () => {
               handleClose()
            },
         }
      )
   }

   useEffect(() => {
      if (reflinkSettings?.markupSettings) {
         if (reflinkSettings.markupSettings.thresholds) {
            setThresholds(
               reflinkSettings.markupSettings.thresholds ? reflinkSettings.markupSettings.thresholds.slice(0, -1) : []
            )
            setAddReflinkData({
               clientContactUuid: reflinkSettings.clientContactUuid || '',
               clientUuid: reflinkSettings.clientUuid || '',
               verificationMethod: reflinkSettings.verificationMethod || 'EMAIL',
               instantOrder: reflinkSettings.instantOrder || false,
               hideGrossPrices: reflinkSettings.hideGrossPrices || false,
               priceType: reflinkSettings.markupSettings.priceType || 'NET',
               emailNotification: reflinkSettings.emailNotification || false,
            })
            setInfinityThresholdValues({
               multiplier:
                  reflinkSettings.markupSettings.thresholds[reflinkSettings.markupSettings.thresholds.length - 1]
                     .multiplier,
               addend:
                  reflinkSettings.markupSettings.thresholds[reflinkSettings.markupSettings.thresholds.length - 1]
                     .addend,
            })
         } else {
            const activeLocation = locations?.locations.find(
               (location) => location.locationSettings.uuid === profileData?.locationUuid
            )
            if (activeLocation) {
               setThresholds(activeLocation.markupSettings.thresholds.slice(0, -1))
               setInfinityThresholdValues({
                  multiplier:
                     activeLocation.markupSettings.thresholds[activeLocation.markupSettings.thresholds.length - 1]
                        .multiplier,
                  addend:
                     activeLocation.markupSettings.thresholds[activeLocation.markupSettings.thresholds.length - 1]
                        .addend,
               })
            }
         }
         const customer = customers?.find((client) => client.uuid === reflinkSettings.clientUuid)
         if (customer) {
            setContactPersons(customer.contacts)
         }
      } else {
         const activeLocation = locations?.locations.find(
            (location) => location.locationSettings.uuid === profileData?.locationUuid
         )
         if (activeLocation) {
            setThresholds(activeLocation.markupSettings.thresholds.slice(0, -1))
            setInfinityThresholdValues({
               multiplier:
                  activeLocation.markupSettings.thresholds[activeLocation.markupSettings.thresholds.length - 1]
                     .multiplier,
               addend:
                  activeLocation.markupSettings.thresholds[activeLocation.markupSettings.thresholds.length - 1].addend,
            })
         }
      }
   }, [reflinkSettings])

   return (
      <>
         <DialogContent>
            <Box sx={{ px: 1, pt: 1, display: 'flex', flexDirection: 'column', gap: '12px' }}>
               <FormControl fullWidth variant="outlined">
                  <InputLabel>{t('CLIENTS_CARTS:selectContact')}</InputLabel>
                  <Select
                     fullWidth
                     value={addReflinkData.clientContactUuid}
                     onChange={(e) =>
                        setAddReflinkData((prevState) => ({ ...prevState, clientContactUuid: e.target.value }))
                     }
                     label={t('CLIENTS_CARTS:selectContact')}
                  >
                     {contactPersons.map((contact) => (
                        <MenuItem key={contact.uuid} value={contact.uuid}>
                           {`${contact.name} ${contact.surname}`}
                        </MenuItem>
                     ))}
                  </Select>
               </FormControl>
               <FormControl fullWidth variant="outlined">
                  <InputLabel>{t('CLIENTS_CARTS:markupSettings.typeOfPriceLabel')}</InputLabel>
                  <Select
                     fullWidth
                     value={addReflinkData.priceType}
                     onChange={(e) =>
                        setAddReflinkData((prevState) => ({ ...prevState, priceType: e.target.value as PriceType }))
                     }
                     label={t('CLIENTS_CARTS:markupSettings.typeOfPriceLabel')}
                  >
                     {priceTypeOptions.map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                           {option.name}
                        </MenuItem>
                     ))}
                  </Select>
               </FormControl>

               <FormControl component="fieldset" variant="outlined">
                  <RadioGroup
                     value={addReflinkData.verificationMethod}
                     onChange={(e) => {
                        setAddReflinkData((prevState) => ({
                           ...prevState,
                           verificationMethod: e.target.value as 'SMS' | 'EMAIL',
                        }))
                     }}
                     row
                  >
                     <Tooltip
                        disableHoverListener={
                           !!customers
                              ?.find((customer) => customer.uuid === addReflinkData.clientUuid)
                              ?.contacts.find((contact) => contact.uuid === addReflinkData.clientContactUuid)
                              ?.phoneNumber
                        }
                        title={t('CLIENTS_CARTS:phoneNumberNotAvailable')}
                     >
                        <Box>
                           <FormControlLabel
                              value="SMS"
                              control={
                                 <Radio
                                    disabled={
                                       !customers
                                          ?.find((customer) => customer.uuid === addReflinkData.clientUuid)
                                          ?.contacts.find(
                                             (contact) => contact.uuid === addReflinkData.clientContactUuid
                                          )?.phoneNumber
                                    }
                                 />
                              }
                              label={t('TRANSLATION:columnNames.phoneNumber')}
                           />
                        </Box>
                     </Tooltip>
                     <Tooltip
                        disableHoverListener={
                           !!customers
                              ?.find((customer) => customer.uuid === addReflinkData.clientUuid)
                              ?.contacts.find((contact) => contact.uuid === addReflinkData.clientContactUuid)?.email
                        }
                        title={t('CLIENTS_CARTS:emailNotAvailable')}
                     >
                        <Box>
                           <FormControlLabel
                              value="EMAIL"
                              control={
                                 <Radio
                                    disabled={
                                       !customers
                                          ?.find((customer) => customer.uuid === addReflinkData.clientUuid)
                                          ?.contacts.find(
                                             (contact) => contact.uuid === addReflinkData.clientContactUuid
                                          )?.email
                                    }
                                 />
                              }
                              label="Email"
                           />
                        </Box>
                     </Tooltip>
                  </RadioGroup>
               </FormControl>
               <FormControlLabel
                  sx={{ maxWidth: 'fit-content ' }}
                  control={
                     <Switch
                        checked={addReflinkData.emailNotification}
                        onChange={(_, checked) =>
                           setAddReflinkData((prevState) => ({ ...prevState, emailNotification: checked }))
                        }
                     />
                  }
                  label={t('CLIENTS_CARTS:markupSettings.emailNotifications')}
               />
               <FormControlLabel
                  sx={{ maxWidth: 'fit-content' }}
                  control={
                     <Switch
                        checked={addReflinkData.instantOrder}
                        onChange={(_, checked) =>
                           setAddReflinkData((prevState) => ({ ...prevState, instantOrder: checked }))
                        }
                     />
                  }
                  label={t('CLIENTS_CARTS:markupSettings.instantOrder')}
               />
               <FormControlLabel
                  sx={{ maxWidth: 'fit-content' }}
                  control={
                     <Switch
                        checked={addReflinkData.hideGrossPrices}
                        onChange={(_, checked) =>
                           setAddReflinkData((prevState) => ({ ...prevState, hideGrossPrices: checked }))
                        }
                     />
                  }
                  label={t('CLIENTS_CARTS:markupSettings.hideGrossPrices')}
               />

               <Box sx={{ maxHeight: 450, overflowY: 'scroll' }}>
                  <Table stickyHeader>
                     <TableHead>
                        <TableRow>
                           <TableCell sx={{ textAlign: 'center' }}>
                              {t('CLIENTS_CARTS:markupSettings.start', {
                                 priceType: t(`TRANSLATION:${profileData?.priceType}`),
                              })}
                           </TableCell>
                           <TableCell sx={{ textAlign: 'center' }}>
                              {t('CLIENTS_CARTS:markupSettings.end', {
                                 priceType: t(`TRANSLATION:${profileData?.priceType}`),
                              })}
                           </TableCell>
                           <TableCell sx={{ textAlign: 'center' }}>
                              {t('CLIENTS_CARTS:markupSettings.multiplier')}
                           </TableCell>
                           <TableCell sx={{ textAlign: 'center' }}>
                              {t('CLIENTS_CARTS:markupSettings.addend')}
                           </TableCell>
                        </TableRow>
                     </TableHead>
                     <TableBody>
                        {thresholds.length > 0 &&
                           thresholds.map((item, index) => {
                              const isLastItem = index === thresholds.length - 1
                              return (
                                 // eslint-disable-next-line react/no-array-index-key
                                 <TableRow key={index}>
                                    <TableCell
                                       sx={{
                                          textAlign: 'center',
                                          '& .MuiTableCell-root': {
                                             px: 3,
                                             py: 0,
                                          },
                                       }}
                                    >
                                       <TextField
                                          sx={{
                                             '& .MuiInputBase-input': {
                                                py: 1,
                                                width: 100,
                                             },
                                          }}
                                          type="Number"
                                          variant="outlined"
                                          disabled
                                          value={item.start}
                                          slotProps={{
                                             htmlInput: {
                                                min: item.start,
                                                step: 0.01,
                                                readonly: true,
                                             },
                                          }}
                                       />
                                    </TableCell>
                                    <TableCell sx={{ textAlign: 'center' }}>
                                       <TextField
                                          sx={{
                                             '& .MuiInputBase-input': {
                                                py: 1,
                                                width: 100,
                                             },
                                          }}
                                          type="Number"
                                          variant="outlined"
                                          error={item.end ? item.start > item.end : false}
                                          value={item.end}
                                          onChange={(e) => {
                                             handleChange(index, 'end', Number(e.target.value))
                                          }}
                                          slotProps={{
                                             htmlInput: {
                                                min: item.start + 0.01,
                                                step: 0.01,
                                                readOnly: true,
                                             },
                                          }}
                                          disabled={!isLastItem}
                                       />
                                    </TableCell>
                                    <TableCell sx={{ textAlign: 'center' }}>
                                       <TextField
                                          sx={{
                                             '& .MuiInputBase-input': {
                                                py: 1,
                                                width: 100,
                                             },
                                          }}
                                          type="Number"
                                          onFocus={(e) => e.target.select()}
                                          variant="outlined"
                                          value={item.multiplier}
                                          onChange={(e) => handleChange(index, 'multiplier', Number(e.target.value))}
                                          slotProps={{
                                             htmlInput: {
                                                min: 1,
                                                step: 0.1,
                                                readOnly: true,
                                             },
                                          }}
                                       />
                                    </TableCell>
                                    <TableCell sx={{ textAlign: 'center' }}>
                                       <TextField
                                          sx={{
                                             '& .MuiInputBase-input': {
                                                py: 1,
                                                width: 100,
                                             },
                                          }}
                                          type="Number"
                                          onFocus={(e) => e.target.select()}
                                          variant="outlined"
                                          value={item.addend}
                                          onChange={(e) => handleChange(index, 'addend', Number(e.target.value))}
                                          slotProps={{
                                             htmlInput: {
                                                min: 0,
                                                step: 1,
                                                readOnly: true,
                                             },
                                          }}
                                       />
                                    </TableCell>
                                 </TableRow>
                              )
                           })}
                        <TableRow>
                           <TableCell
                              sx={{
                                 textAlign: 'center',
                              }}
                           >
                              <TextField
                                 sx={{
                                    '& .MuiInputBase-input': {
                                       py: 1,
                                       width: 100,
                                    },
                                 }}
                                 variant="outlined"
                                 disabled
                                 value={getLastThresholdStartValue()}
                              />
                           </TableCell>
                           <TableCell sx={{ textAlign: 'center' }}>
                              <TextField
                                 variant="outlined"
                                 sx={{
                                    '& .MuiInputBase-input': {
                                       textAlign: 'center',
                                       fontSize: '24px',
                                       paddingY: 0,
                                       width: 100,
                                    },
                                 }}
                                 value="∞"
                                 disabled
                              />
                           </TableCell>
                           <TableCell sx={{ textAlign: 'center' }}>
                              <TextField
                                 sx={{
                                    '& .MuiInputBase-input': {
                                       py: 1,
                                       width: 100,
                                    },
                                 }}
                                 type="Number"
                                 onFocus={(e) => e.target.select()}
                                 variant="outlined"
                                 value={infinityThresholdValues.multiplier}
                                 onChange={(e) =>
                                    setInfinityThresholdValues({
                                       ...infinityThresholdValues,
                                       multiplier: Number(e.target.value),
                                    })
                                 }
                                 slotProps={{
                                    htmlInput: {
                                       min: 1,
                                       step: 0.1,
                                       readOnly: true,
                                    },
                                 }}
                              />
                           </TableCell>
                           <TableCell sx={{ textAlign: 'center' }}>
                              <TextField
                                 sx={{
                                    '& .MuiInputBase-input': {
                                       py: 1,
                                       width: 100,
                                    },
                                 }}
                                 type="Number"
                                 onFocus={(e) => e.target.select()}
                                 variant="outlined"
                                 value={infinityThresholdValues.addend}
                                 onChange={(e) =>
                                    setInfinityThresholdValues({
                                       ...infinityThresholdValues,
                                       addend: Number(e.target.value),
                                    })
                                 }
                                 slotProps={{
                                    htmlInput: {
                                       min: 0,
                                       step: 1,
                                       readOnly: true,
                                    },
                                 }}
                              />
                           </TableCell>
                           <TableCell />
                        </TableRow>
                     </TableBody>
                  </Table>
               </Box>
            </Box>
            <Typography variant="body1" color="red">
               {' '}
               {t('CLIENTS_CARTS:markupSettings.firstTip')}
            </Typography>
         </DialogContent>
         <DialogActions>
            <OutlinedButton variant="outlined" onClick={handleClose}>
               {t('TRANSLATION:cancel')}
            </OutlinedButton>
            <ContainedButton
               sx={{ alignSelf: 'center' }}
               variant="contained"
               onClick={editReflink}
               disabled={
                  isLoading ||
                  (thresholds.length > 0 && !thresholds[thresholds.length - 1].end) ||
                  (thresholds.length > 0 && !!thresholds.find((item) => item.end && item.start > item.end)) ||
                  (thresholds.length > 0 && !!thresholds.find((item) => item.multiplier < 1))
               }
            >
               {t('TRANSLATION:save')}
            </ContainedButton>
         </DialogActions>
      </>
   )
}
